import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CartItemState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAlertCircle } from '@sorare/core/src/atoms/icons/system/iconAlertCircle.ts';
import { iconRefresh } from '@sorare/core/src/atoms/icons/system/iconRefresh.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';
import type { Color } from '@sorare/core/src/style/types';

import { useCart } from '#src/hooks/useCart.ts';

import type { CartItemStateWarning_cartItem } from './__generated__/index.graphql.ts';

const Warning = styled(Horizontal).attrs({ gap: 2 })`
  padding: var(--unit) var(--intermediate-unit);
  border-radius: var(--unit);
  justify-content: space-between;
`;
const Icon = styled.span`
  margin-right: var(--unit);
`;

const states: Record<
  CartItemState,
  | {
      color: Color;
      backgroundColor: Color;
      icon: ReactNode;
      title: ReactNode;
      buttonCta: ReactNode;
    }
  | undefined
> = {
  [CartItemState.REPLACED]: {
    color: 'var(--c-yellow-400)',
    backgroundColor: 'var(--c-surface-caution)',
    icon: <FontAwesomeIcon icon={iconRefresh} />,
    title: <FormattedMessage defaultMessage="Card and price updated" />,
    buttonCta: <FormattedMessage {...glossary.gotIt} />,
  },
  [CartItemState.REMOVED]: {
    color: 'var(--c-red-400)',
    backgroundColor: 'var(--c-surface-error)',
    icon: <FontAwesomeIcon icon={iconAlertCircle} />,
    title: <FormattedMessage defaultMessage="No other instant buy available" />,
    buttonCta: <FormattedMessage {...glossary.remove} />,
  },
  [CartItemState.CURRENT]: undefined,
};

type Props = {
  cartItem: CartItemStateWarning_cartItem;
  trackRemove: () => void;
};
export const CartItemStateWarning = ({ cartItem, trackRemove }: Props) => {
  const { removeFromCart, refreshCart } = useCart();
  const { state } = cartItem || {};

  if (!state) return null;
  const attrs = states[state];

  const onClick = async () => {
    switch (state) {
      case CartItemState.REMOVED: {
        trackRemove?.();
        return removeFromCart.mutate([cartItem.id], cartItem.card.sport);
      }
      case CartItemState.REPLACED:
        return refreshCart.mutate([cartItem.id], cartItem.card.sport);
      default:
        return null;
    }
  };
  const loading = (() => {
    switch (state) {
      case CartItemState.REMOVED:
        return removeFromCart.loading;
      case CartItemState.REPLACED:
        return refreshCart.loading;
      default:
        return false;
    }
  })();

  if (!attrs) return null;
  return (
    <Warning style={{ backgroundColor: attrs.backgroundColor }}>
      <LabelM bold color={attrs.color}>
        <Icon>{attrs.icon}</Icon>
        {attrs.title}
      </LabelM>
      <Button
        loading={loading}
        size="compact"
        color="secondary"
        onClick={onClick}
      >
        <LabelM bold>{attrs.buttonCta}</LabelM>
      </Button>
    </Warning>
  );
};

CartItemStateWarning.fragments = {
  cartItem: gql`
    fragment CartItemStateWarning_cartItem on CartItem {
      id
      state
      card {
        slug
        sport
      }
    }
  ` as TypedDocumentNode<CartItemStateWarning_cartItem>,
};
